import React from 'react';
import { motion } from 'framer-motion';
import { DatabaseIcon, SettingsIcon, BuildingIcon, MessageCircleIcon, CalendarIcon, MapPinIcon, PhoneIcon, MailIcon, LinkedinIcon, InstagramIcon, FacebookIcon, ChevronRightIcon, LayoutGridIcon } from 'lucide-react';
import { sectorPages } from '../data/sectorPages';
const footerLinks = {
  diensten: {
    title: 'Diensten',
    links: [{
      label: 'Websites',
      href: '/websites'
    }, {
      label: 'Digitale vormgeving',
      href: '/vormgeving'
    }, {
      label: 'Hosting',
      href: '/hosting'
    }, {
      label: 'E-mail',
      href: '/email'
    }, {
      label: 'SEO',
      href: '/seo'
    }, {
      label: 'CorePrint',
      href: 'https://coreprint.nl/',
      external: true
    }]
  },
  noordDigital: {
    title: 'Noord Digital',
    links: [{
      label: 'Over ons',
      href: '/over-ons'
    }, {
      label: 'Ons team',
      href: '/over-ons#team'
    }, {
      label: 'Klantcases',
      href: '/#client-cases'
    }, {
      label: 'Kennisbank',
      href: '/bibliotheek'
    }, {
      label: 'Vacatures',
      href: '/sollicitatie'
    }]
  }
};
// De eerste vijf sectorpagina's krijgen een plek in de kolom "Voor wie".
// Alle sectoren staan daarnaast in de strook onderaan, zodat elke pagina
// van de site naar alle sectorpagina's linkt.
const voorWieLinks = sectorPages.slice(0, 5);
const socialLinks = [{
  icon: LinkedinIcon,
  href: 'https://www.linkedin.com/company/noord-digital/',
  label: 'LinkedIn'
}, {
  icon: FacebookIcon,
  href: 'https://www.facebook.com/profile.php?id=61572493568612',
  label: 'Facebook'
}, {
  icon: InstagramIcon,
  href: 'https://www.instagram.com/noord_digital/',
  label: 'Instagram'
}];
const fadeUp = {
  initial: {
    opacity: 0,
    y: 16
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true
  }
};
export function Footer() {
  const jaar = new Date().getFullYear();
  return <footer className="w-full bg-white relative pt-[480px] sm:pt-[420px] md:pt-56 mt-16" data-id="element-335" data-mp="mtrbossp384n">
      {/* Top Curved Banner */}
      <div className="absolute top-0 left-0 w-full -mt-8 px-4 md:px-6 lg:px-8 z-20" data-id="element-336" data-mp="tta0yjibn3yt">
        <div className="container mx-auto max-w-[1200px]" data-id="element-337" data-mp="ys3e2atp4syg">
          <div className="relative rounded-[2.5rem] overflow-hidden bg-[#007AFF] shadow-2xl flex flex-col md:flex-row items-stretch min-h-[320px]" data-id="element-338" data-mp="13s9a037011mc">
            {/* Blue Background with Curve */}
            <div className="absolute inset-0 bg-[#0066CC] z-0" style={{
            clipPath: 'polygon(0 0, 100% 0, 100% 100%, 0 85%)'
          }} data-id="element-339" data-mp="1cmdywh2ko2z8"></div>
            <div className="absolute inset-0 bg-[#007AFF] z-0" style={{
            clipPath: 'polygon(0 0, 80% 0, 60% 100%, 0 100%)'
          }} data-id="element-340" data-mp="1hmig69bb11qi"></div>

            {/* Content Left */}
            <div className="relative z-10 p-10 md:p-16 md:w-[60%] flex flex-col items-start justify-center" data-id="element-341" data-mp="1rgsiaiq1teef">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-8 leading-tight max-w-lg" data-id="element-342" data-mp="3odhu0vm2i3pk">
                Jouw bedrijf een boost geven?
              </h2>
              <a href="#contact" className="inline-flex items-center bg-white text-[#007AFF] font-bold py-4 px-8 rounded-full hover:bg-gray-50 transition-colors shadow-md" data-id="element-343" data-mp="2ageu7jczxz7k">
                Neem contact met ons op{' '}
                <ChevronRightIcon className="ml-2 w-5 h-5" data-id="element-344" data-mp="21kfmbwpppft8" />
              </a>
            </div>

            {/* Image Right */}
            <div className="relative z-10 w-full md:w-[50%] h-64 md:h-auto md:absolute md:right-0 md:top-0 md:bottom-0" data-id="element-345" data-mp="2tehpuh01uuec">
              <div className="absolute inset-0 bg-gradient-to-r from-[#007AFF] to-transparent z-10 hidden md:block w-32" data-id="element-346" data-mp="2pdfq7z280gbu"></div>
              <img src="https://cdn.magicpatterns.com/uploads/kPiXVykaBQH5ZZ9eYcbZFN/image.png" alt="Team overleg Noord Digital" loading="lazy" className="absolute inset-0 w-full h-full object-cover object-center" style={{
              clipPath: 'polygon(15% 0, 100% 0, 100% 100%, 0 100%)'
            }} data-id="element-347" data-mp="2kbgkiij0d7bq" />
            </div>
          </div>
        </div>
      </div>

      {/* Main Footer Content */}
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px] pt-48 pb-12" data-id="element-348" data-mp="2rug3zg9r9ior">
        {/* Top row — Logo + tagline + socials */}
        <motion.div {...fadeUp} className="flex flex-col md:flex-row md:items-start md:justify-between gap-8 mb-16 pb-12 border-b border-gray-100" data-id="element-349" data-mp="2ivb86hpkw3ga">
          <div className="flex items-start gap-4 max-w-md" data-id="element-350" data-mp="2nunqqq9f2od2">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/mbRatHaTnCE2T37GsGP7pj/Norden_portret_300dpi.png" alt="Noord Digital logo" loading="lazy" className="w-14 h-14 object-contain flex-shrink-0 -mt-1" data-id="element-351" data-mp="3bj73pj3evpbh" />
            <div data-id="element-352" data-mp="31khjvkryfusl">
              <h3 className="text-2xl font-bold tracking-tight mb-1 text-[#111827]" data-id="element-353" data-mp="31l0yariec11v">
                Noord Digital
              </h3>
              <p className="text-[#004AC7] font-semibold text-sm mb-2" data-id="element-354" data-mp="1q6dny91t3wkw">
                Online groei op basis van bewijs.
              </p>
              <p className="text-[#6B7280] text-sm leading-relaxed" data-id="element-355" data-mp="1v6i603id4zd8">
                Wij zorgen dat ondernemers in Noord-Holland Noord online
                gevonden worden en professioneel overkomen. Met data als
                fundament, niet als bijzaak.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3 md:pt-1" data-id="element-356" data-mp="1wlcl9fg52dk7">
            {socialLinks.map(social => <a key={social.label} href={social.href} target="_blank" rel="noopener noreferrer" aria-label={`Noord Digital op ${social.label}`} className="w-10 h-10 rounded-full bg-[#F7F8FA] flex items-center justify-center text-[#6B7280] hover:bg-[#004AC7] hover:text-white transition-all duration-200" data-id="element-357" data-mp="1rom5yuc5rlzu">
                <social.icon className="w-4 h-4" data-id="element-358" data-mp="1dojqtfv8j6oa" />
              </a>)}
          </div>
        </motion.div>

        {/* Navigation columns */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 mb-16" data-id="element-359" data-mp="18v8gv5v4m9s5">
          {/* Diensten */}
          <motion.div {...fadeUp} transition={{
          delay: 0.05
        }} data-id="element-360" data-mp="g8dxkca1pgg9">
            <div className="flex items-center gap-2 mb-5" data-id="element-361" data-mp="g6jnjt0bb8qy">
              <SettingsIcon className="w-4 h-4 text-[#004AC7]" data-id="element-362" data-mp="2g9u9rog023t7" />
              <h4 className="text-[#111827] font-semibold text-sm uppercase tracking-wider" data-id="element-363" data-mp="2l9gknr8cv7yu">
                {footerLinks.diensten.title}
              </h4>
            </div>
            <ul className="space-y-3" data-id="element-364" data-mp="2q2szj9n8g0ee">
              {footerLinks.diensten.links.map(link => <li key={link.label} data-id="element-365" data-mp="2uyqebbiukyp2">
                  <a href={link.href} target={link.external ? '_blank' : undefined} rel={link.external ? 'noopener noreferrer' : undefined} className="text-[#6B7280] hover:text-[#004AC7] text-sm transition-colors duration-200 flex items-center gap-2" data-id="element-366" data-mp="1xc18eql0y1xx">
                    <ChevronRightIcon className="w-3 h-3 text-gray-300" data-id="element-367" data-mp="278e23ogkkfso" />
                    {link.label}
                  </a>
                </li>)}
            </ul>
          </motion.div>

          {/* Voor wie — nu de echte sectorpagina's */}
          <motion.div {...fadeUp} transition={{
          delay: 0.1
        }} data-id="element-368" data-mp="3mqfqis1jeqkg">
            <div className="flex items-center gap-2 mb-5" data-id="element-369" data-mp="3msk7aakjbk25">
              <DatabaseIcon className="w-4 h-4 text-[#004AC7]" data-id="element-370" data-mp="1b22lhobl4c1u" />
              <h4 className="text-[#111827] font-semibold text-sm uppercase tracking-wider" data-id="element-371" data-mp="1718w0qihup6u">
                Voor wie
              </h4>
            </div>
            <ul className="space-y-3" data-id="element-372" data-mp="1297pzmbx5up9">
              {voorWieLinks.map(p => <li key={p.slug} data-id="element-373" data-mp="xawdt4y5j83v">
                  <a href={`/website-voor/${p.slug}`} className="text-[#6B7280] hover:text-[#004AC7] text-sm transition-colors duration-200 flex items-center gap-2" data-id="element-374" data-mp="xbpdjlyp0xci">
                    <ChevronRightIcon className="w-3 h-3 text-gray-300 flex-shrink-0" data-id="element-375" data-mp="ncqbim8fnzyh" />
                    {p.sector}
                  </a>
                </li>)}
              <li className="pt-1" data-id="element-376" data-mp="ed3c25z9l482">
                <a href="https://webdesignalkmaar.nl/" target="_blank" rel="noopener noreferrer" className="text-[#9CA3AF] hover:text-[#004AC7] text-xs transition-colors duration-200 flex items-center gap-2" data-id="element-377" data-mp="ee4wh126732g">
                  <ChevronRightIcon className="w-3 h-3 text-gray-200" data-id="element-378" data-mp="28sflb9irlx88" />
                  Webdesign Alkmaar
                </a>
              </li>
            </ul>
          </motion.div>

          {/* Noord Digital */}
          <motion.div {...fadeUp} transition={{
          delay: 0.15
        }} data-id="element-379" data-mp="1mk20kyvb7qx8">
            <div className="flex items-center gap-2 mb-5" data-id="element-380" data-mp="17v5wte0ad02f">
              <BuildingIcon className="w-4 h-4 text-[#004AC7]" data-id="element-381" data-mp="1cusafok10ibr" />
              <h4 className="text-[#111827] font-semibold text-sm uppercase tracking-wider" data-id="element-382" data-mp="1csz121i13k02">
                {footerLinks.noordDigital.title}
              </h4>
            </div>
            <ul className="space-y-3" data-id="element-383" data-mp="12yfhwu07wail">
              {footerLinks.noordDigital.links.map(link => <li key={link.label} data-id="element-384" data-mp="oy4opkbfqju9">
                  <a href={link.href} className="text-[#6B7280] hover:text-[#004AC7] text-sm transition-colors duration-200 flex items-center gap-2" data-id="element-385" data-mp="oz7hjxsbiwct">
                    <ChevronRightIcon className="w-3 h-3 text-gray-300" data-id="element-386" data-mp="2jaufvuaf5exy" />
                    {link.label}
                  </a>
                </li>)}
            </ul>
          </motion.div>

          {/* Contact */}
          <motion.div {...fadeUp} transition={{
          delay: 0.2
        }} data-id="element-387" data-mp="cnn54stryvqz">
            <div className="flex items-center gap-2 mb-5" data-id="element-388" data-mp="7ok19bpj6vkl">
              <MessageCircleIcon className="w-4 h-4 text-[#004AC7]" data-id="element-389" data-mp="7lxsj8qwma0s" />
              <h4 className="text-[#111827] font-semibold text-sm uppercase tracking-wider" data-id="element-390" data-mp="me844hdd22af">
                {footerLinks.contact?.title ?? 'Contact'}
              </h4>
            </div>
            <address className="not-italic" data-id="element-391" data-mp="vc1t178sptnk">
              <ul className="space-y-3" data-id="element-392" data-mp="qea5sqj3ivpp">
                <li data-id="element-393" data-mp="qd8innot2irm">
                  <a href="/#contact" className="text-[#6B7280] hover:text-[#004AC7] text-sm transition-colors duration-200 flex items-center gap-2" data-id="element-394" data-mp="qdqp1d56mdd9">
                    <CalendarIcon className="w-3.5 h-3.5 text-[#004AC7]" data-id="element-395" data-mp="10bwmnijygxcb" />
                    Kennismaking plannen
                  </a>
                </li>
                <li data-id="element-396" data-mp="3735rnvri2cin">
                  <a href="https://maps.google.com/?q=Schuit+8a+1724+BD+Oudkarspel" target="_blank" rel="noopener noreferrer" className="text-[#6B7280] hover:text-[#004AC7] text-sm transition-colors duration-200 flex items-start gap-2" data-id="element-397" data-mp="323sx0oue015i">
                    <MapPinIcon className="w-3.5 h-3.5 text-[#004AC7] mt-0.5 flex-shrink-0" data-id="element-398" data-mp="du43uyhy8k51" />
                    <span data-id="element-399" data-mp="8v9cv99tvp9u">
                      Schuit 8a, 1724 BD Oudkarspel
                      <span className="block text-xs text-[#9CA3AF]" data-id="element-400" data-mp="3x7sml0o1jws">
                        Gemeente Dijk en Waard
                      </span>
                    </span>
                  </a>
                </li>
                <li data-id="element-401" data-mp="wlddd7rpz8wm">
                  <a href="tel:+31653689686" className="text-[#6B7280] hover:text-[#004AC7] text-sm transition-colors duration-200 flex items-center gap-2" data-id="element-402" data-mp="wmy74v67mntf">
                    <PhoneIcon className="w-3.5 h-3.5 text-[#004AC7]" data-id="element-403" data-mp="mw3khpp1xm7g" />
                    06 53689686
                  </a>
                </li>
                <li data-id="element-404" data-mp="1kbzjgti5yj3z">
                  <a href="mailto:info@noorddigital.nl" className="text-[#6B7280] hover:text-[#004AC7] text-sm transition-colors duration-200 flex items-center gap-2" data-id="element-405" data-mp="1kdaosxr9byx7">
                    <MailIcon className="w-3.5 h-3.5 text-[#004AC7]" data-id="element-406" data-mp="1nugl5xxg4kyv" />
                    info@noorddigital.nl
                  </a>
                </li>
              </ul>
            </address>
          </motion.div>
        </div>

        {/* Websites per branche — alle sectorpagina's, op elke pagina van de site.
            Dit geeft de sectorpagina's interne links vanaf de hele website. */}
        <motion.nav {...fadeUp} transition={{
        delay: 0.22
      }} aria-label="Websites per branche" className="border-t border-gray-100 pt-10 mb-12" data-id="element-407" data-mp="26qyc4d4h0gu2">
          <div className="flex items-center gap-2 mb-5" data-id="element-408" data-mp="2fkb8xq5no2lf">
            <LayoutGridIcon className="w-4 h-4 text-[#004AC7]" data-id="element-409" data-mp="2fkut7dis332m" />
            <h4 className="text-[#111827] font-semibold text-sm uppercase tracking-wider" data-id="element-410" data-mp="2kjnvsawj19o0">
              Website laten maken voor jouw branche
            </h4>
          </div>
          <ul className="flex flex-wrap gap-x-6 gap-y-3" data-id="element-411" data-mp="2uhlmr65rtkkb">
            {sectorPages.map(p => <li key={p.slug} data-id="element-412" data-mp="2uf7qitzcu2b6">
                <a href={`/website-voor/${p.slug}`} className="text-[#6B7280] hover:text-[#004AC7] text-sm transition-colors duration-200" data-id="element-413" data-mp="24swz1jnsau5p">
                  {p.sector}
                </a>
              </li>)}
          </ul>
        </motion.nav>

        {/* Bottom bar */}
        <motion.div {...fadeUp} transition={{
        delay: 0.25
      }} className="border-t border-gray-200 pt-8 flex flex-col md:flex-row justify-between items-center gap-4" data-id="element-414" data-mp="2el2pgoye13is">
          <div className="flex items-center gap-3" data-id="element-415" data-mp="2ni39kjefcfgf">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/mbRatHaTnCE2T37GsGP7pj/Norden_portret_300dpi.png" alt="Noord Digital" loading="lazy" className="w-8 h-8 object-contain flex-shrink-0" data-id="element-416" data-mp="2ilcw83u07xyp" />
            <div className="text-center md:text-left" data-id="element-417" data-mp="2sir9xvojm8nb">
              <p className="text-[#6B7280] text-sm" data-id="element-418" data-mp="2sh6em3uemloe">
                © {jaar} Noord Digital. Alle rechten voorbehouden.
              </p>
              {/* Wettelijk verplichte bedrijfsgegevens */}
              <p className="text-[#9CA3AF] text-sm mt-1" data-id="element-419" data-mp="y7chh745dv3v">
                KvK 96071044 · BTW-id NL867457521B01
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4 flex-wrap justify-center" data-id="element-420" data-mp="3eq4t23e4j8j0">
            <a href="/privacy" className="text-[#6B7280] hover:text-[#004AC7] text-sm transition-colors duration-200" data-id="element-421" data-mp="303bmpnml1d2y">
              Privacybeleid
            </a>
            <span className="text-gray-300" data-id="element-422" data-mp="34za5mvp562vz">·</span>
            <a href="/algemene-voorwaarden" className="text-[#6B7280] hover:text-[#004AC7] text-sm transition-colors duration-200" data-id="element-423" data-mp="2r12ckyv87lq3">
              Algemene voorwaarden
            </a>
            <span className="text-gray-300" data-id="element-424" data-mp="2h4h8cfr16enb">·</span>
            <a href="/cookiebeleid" className="text-[#6B7280] hover:text-[#004AC7] text-sm transition-colors duration-200" data-id="element-425" data-mp="2h5scwhsavqfh">
              Cookiebeleid
            </a>
            <span className="text-gray-300" data-id="element-426" data-mp="fdm6p7a57l8o">·</span>
            <a href="/sitemap" className="text-[#6B7280] hover:text-[#004AC7] text-sm transition-colors duration-200" data-id="element-427" data-mp="kbe2icdwrri5">
              Sitemap
            </a>
            <span className="text-gray-300" data-id="element-428" data-mp="2ge0ms0cti95h">·</span>
            <button type="button" onClick={() => {
            window.dispatchEvent(new CustomEvent('nd-open-cookie-settings'));
          }} className="text-[#6B7280] hover:text-[#004AC7] text-sm transition-colors duration-200" data-id="element-429" data-mp="2bfpdbqqfq0ns">
              Cookie-instellingen
            </button>
          </div>
        </motion.div>
      </div>
    </footer>;
}